/**
 * The names a NodePath is built from (`core/string/node_path.cpp`), and the walk
 * `Node::get_node_or_null` takes over them (`scene/main/node.cpp`).
 */
import { nodePathLiteral } from '../variant/variantParser.js';
/**
 * The name segments `NodePath(String)` keeps, in order: only the runs between slashes (`:428-438`),
 * with the absolute marker on `data->absolute`. `.` and `..` are names the walk in `get_node_or_null`
 * reads (`node.cpp:1916-1924`). A `:subname` takes no part in the walk (`node.cpp:1912`), so a node
 * resolver drops it and a caller reproducing `String(NodePath)` keeps it.
 */
export function nodePathNames(path) {
    return path.split('/').filter((segment) => segment !== '');
}
/**
 * The names a node walk reads: {@link nodePathNames} without the `:subname`. The constructor splits
 * at the first `:` and keeps only what precedes it as names (`node_path.cpp:405-427`).
 */
export function nodePathWalkNames(path) {
    return nodePathNames(path.split(':')[0]);
}
/**
 * `String(NodePath(path).simplified())` (node_path.cpp:331-355): each `.` dropped and each name
 * followed by `..` cancelled with it, with `.` left where everything cancels. The text loader
 * stores a `[connection]`'s `from=` and `to=` this way (resource_format_text.cpp:370-371).
 */
export function simplifiedNodePath(path) {
    const colon = path.indexOf(':');
    const namesText = colon === -1 ? path : path.slice(0, colon);
    const subnames = colon === -1 ? '' : path.slice(colon);
    const absolute = namesText.startsWith('/') ? '/' : '';
    return absolute + simplifiedNames(nodePathNames(namesText)).join('/') + subnames;
}
/** `NodePath::simplify` over the names alone, a single name never touched (node_path.cpp:336-338). */
export function simplifiedNames(names) {
    const kept = [...names];
    for (let i = 0; i < kept.length && kept.length > 1; i++) {
        if (kept[i] === '.') {
            kept.splice(i, 1);
            i--;
        }
        else if (i > 0 && kept[i] === '..' && kept[i - 1] !== '.' && kept[i - 1] !== '..') {
            kept.splice(i - 1, 2);
            i -= 2;
            if (kept.length === 0)
                return ['.'];
        }
    }
    return kept;
}
/** `UNIQUE_NODE_PREFIX` (string_name.h:36). */
export const UNIQUE_NODE_PREFIX = '%';
/**
 * Walks `relative` from `base` as `Node::get_node_or_null` walks a NodePath (node.cpp:1912-1949),
 * over the names the constructor kept ({@link nodePathWalkNames}): an extra slash adds nothing
 * and a `:subname` addresses a property. The first `floor` segments spell the scene root, so a
 * `..` below them is null. Without `exists`, a missing name folds away under a later `..`, as
 * Godot's walk never does. A claim can climb out of the walked scene with a leading `..`: the
 * segments then name nodes of the scene above, which no table here holds, so the walk only
 * collects them.
 */
function walk(base, relative, floor, { uniquePaths, exists, isOpaque, instanceTable } = {}) {
    let segments = [...base];
    const parts = nodePathWalkNames(relative);
    for (const [index, part] of parts.entries()) {
        if (part === '.')
            continue;
        if (part === '..') {
            // Nothing above the root: `!current->data.parent` returns nullptr (node.cpp:1919-1922).
            if (segments.length <= floor)
                return null;
            if (segments.at(-1) === '..')
                segments.push('..');
            else
                segments.pop();
            continue;
        }
        if (part.startsWith(UNIQUE_NODE_PREFIX)) {
            if (segments[0] === '..')
                return [...segments, ...parts.slice(index)];
            // A jump through the owner's claim table (node.cpp:1930-1938): the walk restarts at the
            // claimed path, so a following `..` steps up from there. An opaque node reads its own table,
            // which another file fills. At an inherited root that table also lands this file's claims after
            // the base's, so the base's first claim keeps the name (node.cpp:2222-2233).
            const current = segments.join('/');
            const isOpaqueHere = isOpaque?.(current) === true;
            const isBelowRoot = segments.length > floor;
            // An instance root whose table lacks the name defers to its owner's table, here the walk's.
            const isDeferred = isOpaqueHere && isBelowRoot && instanceTable?.(current)?.has(part) === false;
            const claimed = isOpaqueHere && !isDeferred ? undefined : uniquePaths?.get(part);
            if (!claimed)
                return isOpaqueHere && !isDeferred
                    ? { unlisted: [...segments, ...parts.slice(index)].join('/') }
                    : null;
            segments = claimed.split('/');
            continue;
        }
        segments.push(part);
        // A missing child returns nullptr at once (node.cpp:1941-1946), so `Missing/../Real` stops at
        // `Missing`. Only a descent asks: `.` moves nowhere and `..` returns to a visited path.
        if (segments[0] !== '..' && exists && !exists(segments.join('/')))
            return null;
    }
    return segments;
}
/**
 * A relative node path resolved against the base node itself, not its parent, so `"Child"` is a
 * child and `"../Sibling"` a sibling. The base path's first segment is the scene root, the floor:
 * null when the path walks above it. An absolute path measures from the live SceneTree root
 * (node.cpp:1903-1909), which a scene file alone cannot reach, so it reaches nothing.
 */
export function resolveRelativePath(basePath, relative, uniquePaths) {
    if (relative.startsWith('/'))
        return null;
    const end = walk(basePath.split('/'), relative, 1, { uniquePaths });
    return Array.isArray(end) ? end.join('/') : null;
}
/**
 * Whether a path is one the root can walk: an empty `NodePath("")` returns nullptr (node.cpp:1894),
 * and the root, off-tree while instantiate runs, refuses an absolute `/root/…` (node.cpp:1898).
 */
function isRootWalkable(path) {
    return path !== undefined && path !== '' && !path.startsWith('/');
}
/**
 * The node a root-relative path names, spelt from the root without its name (the root is the
 * empty path), null when Godot's walk finds nothing, or {@link UnlistedEnd}. A `.tscn` stores a
 * `parent=`, `from=` and `to=` path (`resource_format_text.cpp:212`) that instantiate resolves from
 * the root with `get_node_or_null` (`packed_scene.cpp:161`), so the root is the floor. Without a
 * `tree`, a missing name folds away under a later `..`, the wider answer, and a `%Name` is null.
 */
export function walkFromRoot(path, tree) {
    // The saver never writes a `%Name` (resource_format_text.cpp:2018), yet on 4.7.2 the loader seats
    // `parent="%Player"` under its claimant and warns only when nothing claims it.
    if (!isRootWalkable(path))
        return null;
    const end = walk([], path, 0, tree);
    return Array.isArray(end) ? end.join('/') : end;
}
/** The path a walk ended on, the unlisted rest for an {@link UnlistedEnd}, or null where it found nothing. */
export function walkedPath(end) {
    return end === null || typeof end === 'string' ? end : end.unlisted;
}
/**
 * A raw `NodePath("…")` property value on the node at `basePath`, resolved against that node as
 * `get_node_or_null` resolves it, or null when it addresses no other node in the scene.
 *
 * @param uniquePaths `%Name` to its claiming path, from `nodePathUniquePaths` over the same tree.
 *   Without it a `%Name` is null: `%` is invalid in a node name (ustring.cpp:5071), so a child
 *   lookup for it never hits.
 */
export function resolveNodePathLiteral(basePath, raw, uniquePaths) {
    const inner = relativePathText(raw);
    return inner === null ? null : resolveRelativePath(basePath, inner, uniquePaths);
}
/**
 * A literal's path text, or null when it addresses no node in this file by construction: an
 * absent value, `.`, or an absolute `/root/…` path, which measures from the live SceneTree.
 */
function relativePathText(raw) {
    if (!raw)
        return null;
    // Whole property values arrive here; text that is neither spelling is taken
    // as the path itself.
    const inner = (nodePathLiteral(raw) ?? raw).trim();
    if (inner === '' || inner === '.')
        return null;
    if (inner.startsWith('/'))
        return null;
    return inner;
}
/**
 * The `%Name` segments of this literal that nothing claims, so a caller reporting a dangling path
 * tells that null apart from the deliberate nulls above, which never report here.
 */
export function unclaimedUniqueNames(raw, uniquePaths) {
    const inner = relativePathText(raw);
    if (inner === null)
        return [];
    return nodePathWalkNames(inner).filter((segment) => segment.startsWith(UNIQUE_NODE_PREFIX) && !uniquePaths?.has(segment));
}
//# sourceMappingURL=nodePath.js.map